import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "../../shared/Seo";
import { WebSiteJsonLd, OrganizationJsonLd, VideoGameJsonLd } from "../../shared/JsonLd";
import { DONATE_URL, DONATE_ENABLED } from "../../shared/donate";
import { DISCORD_INVITE_URL } from "../../app/Footer";
import styles from "./AboutPage.module.scss";
import { asset } from "../../shared/portal-env";
import { ON_PORTAL } from "../../shared/portal-env";

const SITE_URL = "https://achtungroyale.io";
const CONTACT_EMAIL = "hello@achtungroyale.io";

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Achtung Royale: Achtung, die Kurve! reborn"
        description="The story behind Achtung Royale, a free browser remake of the 1995 classic Achtung, die Kurve! (Zatacka), made by a tiny Swedish studio. No download needed."
        canonical={`${SITE_URL}/about/`}
      />
      <WebSiteJsonLd />
      <OrganizationJsonLd />
      <VideoGameJsonLd />

      <div className={styles.about}>
        <header className={styles.hero}>
          <img className={styles.heroLogo} src={asset("/logo-mark.svg")} alt="" />
          <h1>Achtung Royale</h1>
          <p className={styles.tagline}>The classic Achtung die Kurve, reborn and reworked for online play.</p>
        </header>

        <p className={styles.lead}>
          Achtung Royale is a free, modern tribute to the classic <em>Achtung, die Kurve!</em> Our
          goal is simple: preserve the pure, addictive chaos of the original, while giving it
          the modern online arena it always deserved.
        </p>

        <h2>A 90s classic, reborn</h2>
        <p>
          The original <em>Achtung, die Kurve!</em> (also known as <em>Zatacka</em>) was a
          freeware MS-DOS game from 1995: a handful of players steering ever-growing worms
          with just two keys, each trying to outlast the rest. It built a devoted following and inspired a whole genre.{" "}
          <a href="https://en.wikipedia.org/wiki/Achtung,_die_Kurve!" target="_blank" rel="noopener noreferrer">
            Read more about Achtung, die Kurve!
          </a>
        </p>
        {/* the kurve.se note and its screenshot side by side (stacked on a phone);
            not on a game site's build, which may not point to other games */}
        {!ON_PORTAL && (
          <div className={styles.aside}>
          <p>
            Curious what the original <em>Achtung, die Kurve!</em> looked like? Simon Alling has built
            a faithful remake on{" "}
            <a href="https://kurve.se" target="_blank" rel="noopener noreferrer">kurve.se</a>, as true to
            the 1995 game as you can get. If you ever feel nostalgic and want to play{" "}
            <em>Achtung, die Kurve!</em> the same way you did as a kid, that's the best place to do it!
          </p>
          <figure className={styles.figure}>
            <a href="https://kurve.se" target="_blank" rel="noopener noreferrer">
              <img src={asset("/about/kurve-se.jpg")} width={1100} height={893} loading="lazy" alt="A round of the original Achtung, die Kurve! on kurve.se: orange and red trails on a black arena, the scores in the margin." />
            </a>
            <figcaption>The original, recreated on kurve.se</figcaption>
          </figure>
          </div>
        )}

        <h2>Made by Skill Check Games</h2>
        <p>
          Achtung Royale is made by{" "}
          <a href="https://skillcheckgames.com" target="_blank" rel="noopener noreferrer">Skill Check Games</a>,
          a tiny Swedish studio of two good friends, Mathias and Erik, who both put hours into
          the original on a shared keyboard with friends back in high school. The first version of Achtung Royale,
          then known as Achtung Online, went live in 2011 as a university project, and in 2026 it was rebuilt from
          the ground up and reborn as Achtung Royale.
        </p>
        <p>
          Plenty of friends left fingerprints along the way since 2011: Lucas helped build that very
          first version together with Mathias, Henrik gave the game its first visual profile, and Erik engineered the latest version of the bots
          you can still sharpen your skills against offline today. The engine is
          our own, written from scratch.
        </p>

        <h2>Free, and fair</h2>
        <p>
          The game is free, runs right in your browser, and even works offline. No login is
          required to play. We aim to keep the game faithful to the original while expanding on
          ranked competition, gameplay variety, and modern quality of life.
        </p>
        {/* (a game site's build has no shop and no donations: shared/portal-env.ts) */}
        {!ON_PORTAL && (<>
          <p>
            It stays alive through donations and cosmetic purchases. We strongly distance
            ourselves from the pay-to-win practices plaguing the gaming industry today. Your wallet
            will never give you an edge in the arena.{" "}
            {DONATE_ENABLED ? (
              <>
                If this speaks to you, we would appreciate any{" "}
                <a href={DONATE_URL} target="_blank" rel="noopener noreferrer">
                  donations on Ko-fi
                </a>
                , or even better, purchases through our{" "}
                <Link to="/shop">cosmetic shop</Link>.{" "}
              </>
            ) : (
              <>
                If this speaks to you, we would appreciate any purchases through our{" "}
                <Link to="/shop">cosmetic shop</Link>.{" "}
              </>
            )}
            We don't sell your data.
          </p>
          <div className={styles.support}>
            {DONATE_ENABLED && (
              <a className={`app-btn app-btn-primary ${styles.supportBtn}`} href={DONATE_URL} target="_blank" rel="noopener noreferrer">
                <span>Support us with a beer</span>
                <span className={styles.btnEmoji} aria-hidden="true">🍺</span>
              </a>
            )}
            {DONATE_ENABLED && <span className={styles.orBy}>or by</span>}
            <Link className={`app-btn app-btn-primary ${styles.supportBtn}`} to="/shop">
              <span>Pimping your worm in the shop</span>
              <span className={styles.btnEmoji} aria-hidden="true">🛍️</span>
            </Link>
          </div>
        </>)}

        <h2>Contact</h2>
        <p>
          Questions, bugs, or feedback? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
          or come say hi on our{" "}
          <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">Discord</a>.
        </p>

        <h2>Credits</h2>
        <p>
          <b>Music:</b> the Pink Bloom Pack by{" "}
          <a href="https://www.davidkbd.com/" target="_blank" rel="noopener noreferrer">DavidKBD</a>{" "}
          (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>)
        </p>
        <p>
          <b>Sounds:</b> the Universal UI Soundpack by{" "}
          <a href="https://nathangibson.myportfolio.com" target="_blank" rel="noopener noreferrer">Nathan Gibson</a>{" "}
          (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>)
        </p>
      </div>
    </>
  );
}
